"use client";

import { useEffect, useCallback, useRef, MutableRefObject } from "react";
import { validateFields } from "@/lib/utils";
import { FormState, Section, VariantConfig, CategoryItem, NestedItem } from "../types";
import {
  showErrorToast,
  showSuccessToast,
  showValidationErrorToast,
  extractFieldsFromData,
  buildDefaultFieldValues,
  extractCategoriesFromResponse,
  unwrapAdminApiPayload,
  normalizeCategoryItems,
  processThreeLevelCategories,
  getExpandedCategoryIds,
  getExpandedNestedIds,
  buildSavePayload,
  isValidId,
  prepareSectionPreviewData,
} from "../utils";

interface UseComponentDataProps {
  selectedSection: Section;
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  pageId?: string;
  pageComponentId?: string;
  variantConfig: VariantConfig | null;
  componentFieldsConfig: any[];
  dispatch: any;
  reduxState: any;
  currentUserId?: string | number;
  onSave: (section: Section) => void;
  prevPageComponentIdRef: MutableRefObject<{ pageComponentId?: string; componentId?: string }>;
}

// ============================================================================
// Helper: Fetch category with nested items (three-level)
// ============================================================================
const fetchCategoryWithNestedItems = async (
  category: CategoryItem,
  fetchNestedItemsMethod: (id: number) => Promise<any>,
  fetchItemsMethod: (id: number) => Promise<any>
): Promise<CategoryItem> => {
  try {
    const nestedResponse = await fetchNestedItemsMethod(category.id);
    const semesters = nestedResponse?.data || nestedResponse || [];

    const semestersWithItems = await Promise.all(
      (Array.isArray(semesters) ? semesters : []).map(async (semester: NestedItem) => {
        try {
          const itemResponse = await fetchItemsMethod(semester.id);
          const items = itemResponse?.data || itemResponse || [];
          return { ...semester, items: Array.isArray(items) ? items : [] };
        } catch {
          return { ...semester, items: [] };
        }
      })
    );

    return { ...category, semesters: semestersWithItems };
  } catch {
    return { ...category, semesters: [] };
  }
};

// ============================================================================
// Helper: Fetch category with direct items (two-level)
// ============================================================================
const fetchCategoryWithItems = async (
  category: CategoryItem,
  fetchItemsMethod: (id: number) => Promise<any>
): Promise<CategoryItem> => {
  try {
    const itemResponse = await fetchItemsMethod(category.id);
    const unwrapped = unwrapAdminApiPayload(itemResponse) ?? itemResponse;
    const items = Array.isArray(unwrapped) ? unwrapped : [];
    return { ...category, items };
  } catch {
    return { ...category, items: [] };
  }
};

// ============================================================================
// Helper: Update form state with categories
// ============================================================================
const updateCategoriesState = (
  setFormState: React.Dispatch<React.SetStateAction<FormState>>,
  categories: CategoryItem[],
  includeNestedExpansion = false
) => {
  setFormState((prev) => ({
    ...prev,
    categories,
    expandedCategoryIds: getExpandedCategoryIds(categories),
    ...(includeNestedExpansion && { expandedNestedIds: getExpandedNestedIds(categories) }),
  }));
};

const mergeVisionariesHybridPoolBucket = (
  categories: CategoryItem[],
  poolProfiles: unknown[] | undefined | null
): CategoryItem[] => {
  const rest = categories.filter((c) => c.id !== -1 && !c.is_uncategorized_bucket);
  const bucket: CategoryItem = {
    id: -1,
    name: "",
    order_index: 0,
    is_uncategorized_bucket: true,
    items: Array.isArray(poolProfiles) ? (poolProfiles as CategoryItem["items"]) : [],
  };
  return [bucket, ...rest];
};

/** GET returned no row or failed: apply defaults, but keep in-flight edits (async race with typing). */
const applyNoRowOrErrorLoadState = (
  setFormState: React.Dispatch<React.SetStateAction<FormState>>,
  componentFieldsConfig: any[]
) => {
  setFormState((prev) => {
    const defaults = buildDefaultFieldValues(componentFieldsConfig);
    const cur = prev.componentFields || {};
    const userEdited = Object.keys(cur).some((key) => !Object.is(cur[key], defaults[key]));
    if (userEdited) {
      return {
        ...prev,
        componentFields: { ...defaults, ...cur },
        parentId: null,
        categories: [],
      };
    }
    return {
      ...prev,
      componentFields: defaults,
      parentId: null,
      categories: [],
    };
  });
};

/** Clone / onSave can refresh `selectedSection.data` while Form3 local state is still empty — merge config keys so Save/Update sends a full row. */
const mergeFormFieldsWithSectionData = (
  fieldsConfig: any[],
  formFields: Record<string, any>,
  sectionData: unknown
): Record<string, any> => {
  const base: Record<string, any> = {};
  if (sectionData && typeof sectionData === "object" && !Array.isArray(sectionData)) {
    const d = sectionData as Record<string, any>;
    fieldsConfig.forEach((f: any) => {
      if (Object.prototype.hasOwnProperty.call(d, f.fieldName)) {
        base[f.fieldName] = d[f.fieldName];
      }
    });
  }
  return { ...base, ...formFields };
};

/**
 * Eligibility row id for UPDATE — never the page_component id.
 * Preview sometimes sets `data.id` / `componentId` to pageComponentId before the real row exists.
 */
const resolveForm3PersistedEntityId = (
  formParentId: number | null,
  formFieldsId: unknown,
  sectionData: unknown,
  sectionComponentId: string | undefined,
  pageComponentId: string | undefined
): number | undefined => {
  const pageCompNum =
    pageComponentId != null && pageComponentId !== "" && isValidId(pageComponentId)
      ? Number(pageComponentId)
      : null;

  const pick = (raw: unknown): number | undefined => {
    if (!isValidId(raw as string | number | null | undefined)) return undefined;
    const n = Number(raw);
    if (pageCompNum != null && n === pageCompNum) return undefined;
    return n;
  };

  const fromParent = pick(formParentId);
  if (fromParent !== undefined) return fromParent;

  const fromFields = pick(formFieldsId);
  if (fromFields !== undefined) return fromFields;

  if (sectionData && typeof sectionData === "object" && !Array.isArray(sectionData)) {
    const fromData = pick((sectionData as Record<string, any>).id);
    if (fromData !== undefined) return fromData;
  }

  return pick(sectionComponentId);
};

// ============================================================================
// Main Hook
// ============================================================================
export const useComponentData = ({
  selectedSection,
  formState,
  setFormState,
  pageId,
  pageComponentId,
  variantConfig,
  componentFieldsConfig,
  dispatch,
  reduxState,
  currentUserId,
  onSave,
  prevPageComponentIdRef,
}: UseComponentDataProps) => {
  // Tracks which sidebar section we last loaded for. Cannot rely on prevPageComponentIdRef for
  // "widget changed" because useFormState's effect runs first in the same tick and updates that
  // ref before this effect runs — so widgetChanged was always false and loadComponentData was
  // skipped in favor of fetchData (which never fills componentFields from getMethod).
  const prevLoadedSectionIdRef = useRef<string | undefined>(undefined);
  // Run GET-by-component only once per (section id + page component + variant component id).
  // Do not key off redux `id` being empty: that stays true until the first save and would
  // re-trigger load after every field edit (effect deps), wiping edits.
  const form3DataContextRef = useRef<string>("");
  // Avoid re-running fetchData / useEffect when onSave only updates selectedSection.data (preview).
  const selectedSectionRef = useRef(selectedSection);
  selectedSectionRef.current = selectedSection;

  // Merging into Redux in fetchFromSectionData / fetchFromApi must not list `reduxState` in
  // useCallback deps: each dispatch updates the slice → new reduxState → new callbacks → new
  // `fetchData` → Form3's loader effect runs again → hasComponentData calls fetchData in a loop
  // ("Maximum update depth exceeded").
  const reduxStateRef = useRef(reduxState);
  reduxStateRef.current = reduxState;

  const getMethod = variantConfig?.getMethod;
  const createMethod = variantConfig?.createMethod;
  const updateMethod = variantConfig?.updateMethod;
  const fetchCategoriesMethod = (variantConfig as any)?.fetchCategoriesMethod;
  const fetchItemsMethod = (variantConfig as any)?.fetchItemsMethod;
  const fetchNestedItemsMethod = (variantConfig as any)?.fetchNestedItemsMethod;
  const childItemsFieldName = (variantConfig as any)?.childItemsFieldName || "categories";

  // ---------------------------------------------------------------------------
  // Load Component Data
  // ---------------------------------------------------------------------------
  const loadComponentData = useCallback(async () => {
    if (!getMethod || !pageComponentId) {
      if (!pageComponentId) applyNoRowOrErrorLoadState(setFormState, componentFieldsConfig);
      return;
    }

    if (!isValidId(pageComponentId)) {
      console.warn("Invalid pageComponentId:", pageComponentId);
      return;
    }

    try {
      const response = await getMethod(Number(pageComponentId));
      const raw = unwrapAdminApiPayload(response);
      const componentData: Record<string, any> =
        raw != null && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, any>) : {};
      const hasRow =
        componentData &&
        Object.keys(componentData).length > 0 &&
        isValidId(componentData.id as string | number | undefined);

      if (hasRow) {
        variantConfig?.setAction && dispatch(variantConfig.setAction(componentData));
        const fields = extractFieldsFromData(componentData, componentFieldsConfig);
        setFormState((prev) => ({
          ...prev,
          componentFields: fields,
          parentId: componentData.id || null,
        }));
      } else {
        applyNoRowOrErrorLoadState(setFormState, componentFieldsConfig);
      }
    } catch (error) {
      console.error("Failed to load component data:", error);
      applyNoRowOrErrorLoadState(setFormState, componentFieldsConfig);
    }
  }, [getMethod, pageComponentId, componentFieldsConfig, variantConfig, dispatch, setFormState]);

  // ---------------------------------------------------------------------------
  // Fetch Categories - Use existing section data
  // ---------------------------------------------------------------------------
  const fetchFromSectionData = useCallback(() => {
    const data = selectedSectionRef.current?.data;
    const { categories: categoriesList, parentId, componentData } = extractCategoriesFromResponse(
      data,
      childItemsFieldName
    );

    if (categoriesList.length === 0) {
      return false;
    }

    setFormState((prev) => ({
      ...prev,
      parentId: (parentId ?? componentData?.id) ?? prev.parentId,
    }));

    if (variantConfig?.setAction && componentData) {
      const { categories, semesters, ...restData } = componentData;
      // Slice setters replace the whole object; merge so partial section/API data never wipes live fields.
      dispatch(
        variantConfig.setAction({ ...(reduxStateRef.current || {}), ...restData } as any)
      );
    }

    if (componentFieldsConfig.length > 0) {
      const fields = extractFieldsFromData(data, componentFieldsConfig);
      setFormState((prev) => ({ ...prev, componentFields: fields }));
    }

    const processedCategories =
      categoriesList.length > 0
        ? fetchNestedItemsMethod
          ? processThreeLevelCategories(categoriesList)
          : normalizeCategoryItems(categoriesList)
        : [];

    updateCategoriesState(setFormState, processedCategories, Boolean(fetchNestedItemsMethod));
    return true;
  }, [
    childItemsFieldName,
    variantConfig,
    dispatch,
    componentFieldsConfig,
    fetchNestedItemsMethod,
    setFormState,
  ]);

  // ---------------------------------------------------------------------------
  // Fetch Categories - From API
  // ---------------------------------------------------------------------------
  const fetchFromApi = useCallback(async () => {
    if (!fetchCategoriesMethod || !isValidId(pageComponentId)) {
      setFormState((prev) => ({ ...prev, categories: [], parentId: null }));
      return;
    }

    try {
      const response = await fetchCategoriesMethod(Number(pageComponentId));
      const { categories: categoriesList, parentId, componentData } = extractCategoriesFromResponse(
        response,
        childItemsFieldName
      );

      const resolvedParentId = parentId ?? (componentData as Record<string, unknown> | null)?.id ?? null;
      resolvedParentId != null &&
        setFormState((prev) => ({ ...prev, parentId: Number(resolvedParentId) }));

      if (variantConfig?.setAction && componentData && Object.keys(componentData).length > 0) {
        const { categories, semesters, ...restData } = componentData;
        dispatch(
          variantConfig.setAction({ ...(reduxStateRef.current || {}), ...restData } as any)
        );
      }

      if (categoriesList.length === 0) {
        if (
          (variantConfig as VariantConfig | null)?.visionariesHybridForm === true &&
          resolvedParentId != null
        ) {
          const poolProfiles = (componentData as Record<string, unknown> | null)?.pool_profiles;
          const merged = mergeVisionariesHybridPoolBucket([], poolProfiles as unknown[]);
          setFormState((prev) => ({
            ...prev,
            categories: merged,
            expandedCategoryIds: getExpandedCategoryIds(merged),
          }));
        } else {
          setFormState((prev) => ({ ...prev, categories: categoriesList }));
        }
        return;
      }

      let categoriesAfterStrategy: CategoryItem[] = [];

      // Fetch strategy based on structure level
      const fetchStrategies = {
        threeLevel: {
          canUse: () => fetchNestedItemsMethod && fetchItemsMethod,
          execute: async () => {
            const categories = await Promise.all(
              categoriesList.map((cat: CategoryItem) =>
                fetchCategoryWithNestedItems(cat, fetchNestedItemsMethod, fetchItemsMethod)
              )
            );
            categoriesAfterStrategy = categories;
            updateCategoriesState(setFormState, categories, true);
          },
        },
        twoLevel: {
          canUse: () => fetchItemsMethod,
          execute: async () => {
            const categories = await Promise.all(
              categoriesList.map((cat: CategoryItem) => {
                if (cat.id === -1 || cat.is_uncategorized_bucket) {
                  return Promise.resolve(normalizeCategoryItems([cat])[0] as CategoryItem);
                }
                return fetchCategoryWithItems(cat, fetchItemsMethod);
              })
            );
            categoriesAfterStrategy = categories;
            updateCategoriesState(setFormState, categories);
          },
        },
        flat: {
          canUse: () => true,
          execute: async () => {
            categoriesAfterStrategy = categoriesList;
            setFormState((prev) => ({ ...prev, categories: categoriesList }));
          },
        },
      };

      const strategy = Object.values(fetchStrategies).find((s) => s.canUse());
      await strategy?.execute();

      if ((variantConfig as VariantConfig | null)?.visionariesHybridForm) {
        const poolProfiles = (componentData as Record<string, unknown> | null)?.pool_profiles;
        setFormState((prev) => {
          const source =
            categoriesAfterStrategy.length > 0 ? categoriesAfterStrategy : prev.categories;
          const merged = mergeVisionariesHybridPoolBucket(source, poolProfiles as unknown[]);
          return {
            ...prev,
            categories: merged,
            expandedCategoryIds: getExpandedCategoryIds(merged),
          };
        });
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      showErrorToast("Failed to fetch data");
    }
  }, [
    fetchCategoriesMethod,
    fetchItemsMethod,
    fetchNestedItemsMethod,
    pageComponentId,
    variantConfig,
    dispatch,
    childItemsFieldName,
    setFormState,
  ]);

  // ---------------------------------------------------------------------------
  // Fetch Data (main entry point)
  // ---------------------------------------------------------------------------
  const fetchData = useCallback(
    async (forceFetch = false) => {
      if ((variantConfig as VariantConfig | null)?.visionariesHybridForm === true) {
        await fetchFromApi();
        return;
      }
      const section = selectedSectionRef.current;
      const canUseSectionData = !forceFetch && section?.data && section?.componentId;
      if (canUseSectionData && fetchFromSectionData()) return;
      await fetchFromApi();
    },
    [fetchFromSectionData, fetchFromApi, variantConfig]
  );

  // ---------------------------------------------------------------------------
  // Component Field Update
  // ---------------------------------------------------------------------------
  const handleComponentFieldsUpdate = useCallback(
    (updatedData: any) => {
      setFormState((prev) => ({ ...prev, componentFields: updatedData }));
      // Keep page slice in sync so PreviewRenderer + getPreviewData show edits before "Update Component" (matches Form_1).
      if (variantConfig?.setAction) {
        dispatch(
          variantConfig.setAction({
            ...(reduxState || {}),
            ...updatedData,
            id: reduxState?.id ?? updatedData?.id ?? "",
            status:
              (reduxState as any)?.status ??
              (updatedData as any)?.status ??
              false,
          } as any)
        );
      }
      if (onSave && pageComponentId) {
        const merged = { ...(reduxState || {}), ...updatedData };
        const previewId = isValidId(merged.id) ? Number(merged.id) : Number(pageComponentId);
        onSave(
          prepareSectionPreviewData(
            selectedSection,
            previewId,
            { ...merged, id: previewId },
            formState.categories,
            childItemsFieldName
          )
        );
      }
    },
    [
      setFormState,
      variantConfig,
      dispatch,
      reduxState,
      onSave,
      pageComponentId,
      selectedSection,
      formState.categories,
      childItemsFieldName,
    ]
  );

  // ---------------------------------------------------------------------------
  // Load existing parent id from API (no side effects on missing row)
  // ---------------------------------------------------------------------------
  const fetchExistingParentId = useCallback(async (): Promise<number | null> => {
    if (!getMethod || !pageComponentId) return null;
    const componentId = Number(pageComponentId);
    if (isNaN(componentId) || componentId <= 0) return null;
    try {
      const response = await getMethod(componentId);
      const data = response?.data || response || {};
      const id = data?.id != null && data.id !== "" ? Number(data.id) : null;
      return id && !isNaN(id) ? id : null;
    } catch {
      return null;
    }
  }, [getMethod, pageComponentId]);

  // ---------------------------------------------------------------------------
  // Ensure parent row exists (e.g. faq) so categories/items can be added without
  // requiring a separate "Save component" click first.
  // ---------------------------------------------------------------------------
  const ensureComponentParent = useCallback(async (): Promise<number | null> => {
    if (!variantConfig) return null;

    if (formState.parentId) {
      return formState.parentId;
    }
    if (!pageComponentId || !createMethod) {
      return null;
    }

    const existing = await fetchExistingParentId();
    if (existing) {
      const response = getMethod
        ? await getMethod(Number(pageComponentId))
        : null;
      const data = response?.data || response || {};
      if (data && Object.keys(data).length > 0) {
        variantConfig.setAction && dispatch(variantConfig.setAction(data));
      }
      setFormState((prev) => ({ ...prev, parentId: existing }));
      return existing;
    }

    const { errors, messages } = validateFields(componentFieldsConfig, formState.componentFields);
    if (Object.keys(errors).length > 0) {
      setFormState((prev) => ({ ...prev, componentFieldErrors: errors, componentFieldMessages: messages }));
      showValidationErrorToast();
      return null;
    }

    try {
      const payload = buildSavePayload(formState.componentFields, pageId, currentUserId, false, {
        variant: selectedSection.variant,
        component_id: Number(pageComponentId),
      });
      const response = await createMethod(payload);
      const newId = response?.data?.id ?? response?.id;
      if (newId == null) {
        showErrorToast("Failed to get parent id after create");
        return null;
      }
      const id = Number(newId);
      setFormState((prev) => ({ ...prev, parentId: id }));
      if (getMethod) {
        const full = await getMethod(Number(pageComponentId));
        const d = full?.data || full;
        if (d && Object.keys(d).length > 0) {
          variantConfig.setAction && dispatch(variantConfig.setAction(d));
        }
      } else {
        variantConfig.setAction && dispatch(variantConfig.setAction({ id, ...formState.componentFields }));
      }
      await updatePreview(id);
      await fetchData(true);
      return id;
    } catch (error: any) {
      showErrorToast(error?.message || "Failed to create parent record");
      return null;
    }
  }, [
    variantConfig,
    formState.parentId,
    formState.componentFields,
    componentFieldsConfig,
    pageId,
    pageComponentId,
    currentUserId,
    selectedSection,
    createMethod,
    getMethod,
    dispatch,
    fetchData,
    setFormState,
    fetchExistingParentId,
  ]);

  // ---------------------------------------------------------------------------
  // Component Save
  // ---------------------------------------------------------------------------
  const handleComponentSave = useCallback(async () => {
    if (!variantConfig) {
      showErrorToast("Component configuration not found");
      return;
    }

    const fieldsForSave = mergeFormFieldsWithSectionData(
      componentFieldsConfig,
      formState.componentFields,
      selectedSection?.data
    );

    const { errors, messages } = validateFields(componentFieldsConfig, fieldsForSave);
    setFormState((prev) => ({
      ...prev,
      componentFieldErrors: errors,
      componentFieldMessages: messages,
    }));

    if (Object.keys(errors).length > 0) {
      showValidationErrorToast();
      return;
    }

    const existingFaqId = await fetchExistingParentId();
    const isUpdate = existingFaqId != null;

    try {
      const persistedId = resolveForm3PersistedEntityId(
        formState.parentId,
        fieldsForSave.id ?? formState.componentFields?.id,
        selectedSection?.data,
        selectedSection?.componentId,
        pageComponentId
      );
      const isUpdate = isValidId(persistedId);
      const payload = buildSavePayload(fieldsForSave, pageId, currentUserId, isUpdate, {
        variant: selectedSection.variant,
        ...(pageComponentId ? { component_id: Number(pageComponentId) } : {}),
      });
      if (variantConfig?.includeCategoriesInParentSave) {
        (payload as Record<string, unknown>).categories = formState.categories;
      }

      // Save operations using object mapping
      const saveOperations = {
        update: {
          canExecute: () => isUpdate && updateMethod,
          execute: async () => {
            await updateMethod!(Number(persistedId), payload);
            return persistedId;
          },
          message: "Component updated successfully",
        },
        create: {
          canExecute: () => !isUpdate && createMethod,
          execute: async () => {
            const response = await createMethod!(payload);
            const created = unwrapAdminApiPayload(response);
            const id =
              (created != null && typeof created === "object" && "id" in created
                ? (created as { id?: number }).id
                : undefined) ??
              (response as any)?.data?.id ??
              (response as any)?.id;
            if (!id) throw new Error("Failed to get ID from response");
            return Number(id);
          },
          message: "Component created successfully",
        },
      };

      const operation = Object.values(saveOperations).find((op) => op.canExecute());
      if (!operation) throw new Error("Save method not configured");

      const componentId = await operation.execute();
      showSuccessToast(operation.message);

      const mergedSlice = {
        ...(reduxState || {}),
        ...fieldsForSave,
        id: componentId,
        status:
          (reduxState as any)?.status ??
          (fieldsForSave as any)?.status ??
          false,
      };

      setFormState((prev) => ({
        ...prev,
        parentId: componentId,
        componentFields: { ...fieldsForSave, id: componentId },
      }));

      if (variantConfig?.setAction) {
        dispatch(variantConfig.setAction(mergedSlice as any));
      }

      // Update preview
      await updatePreview(componentId, { ...fieldsForSave, id: componentId });
      await fetchData(true);
    } catch (error: any) {
      showErrorToast(error?.message || "Failed to save component");
    }
  }, [
    variantConfig,
    componentFieldsConfig,
    formState.componentFields,
    formState.parentId,
    formState.categories,
    reduxState,
    pageId,
    pageComponentId,
    currentUserId,
    selectedSection,
    createMethod,
    updateMethod,
    fetchData,
    setFormState,
    dispatch,
  ]);

  // ---------------------------------------------------------------------------
  // Helper: Update Preview
  // ---------------------------------------------------------------------------
  const updatePreview = async (
    componentId: number,
    componentFieldsSnapshot?: Record<string, any>
  ) => {
    if (!onSave || !getMethod || !pageComponentId) return;

    const fieldsSnapshot = componentFieldsSnapshot ?? formState.componentFields;

    try {
      const response = await getMethod(Number(pageComponentId));
      const raw = unwrapAdminApiPayload(response);
      const data: Record<string, any> =
        raw != null && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, any>) : {};

      const categoriesList =
        data[childItemsFieldName] ||
        data.categories ||
        data.semesters ||
        selectedSection?.data?.[childItemsFieldName] ||
        selectedSection?.data?.categories ||
        selectedSection?.data?.semesters ||
        formState.categories ||
        [];

      onSave(
        prepareSectionPreviewData(
          selectedSection,
          componentId,
          { ...data, ...fieldsSnapshot, id: componentId },
          categoriesList,
          childItemsFieldName
        )
      );
    } catch (error) {
      console.error("Failed to fetch updated data for preview:", error);
      onSave(
        prepareSectionPreviewData(
          selectedSection,
          componentId,
          fieldsSnapshot,
          formState.categories,
          childItemsFieldName
        )
      );
    }
  };

  // ---------------------------------------------------------------------------
  // Single Effect: Handle all data loading scenarios
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const sectionKeyChanged =
      prevLoadedSectionIdRef.current !== selectedSection?.id;

    const componentIdChanged =
      selectedSection?.componentId !== prevPageComponentIdRef.current?.componentId;
    const widgetChanged =
      prevPageComponentIdRef.current?.pageComponentId !== pageComponentId || componentIdChanged;

    // Use section row id from `data`, not `selectedSection.componentId` — preview/save rewrites
    // componentId and was re-running load + wiping/clobbering after Update. Clone adds data.id
    // without Form3 local state catching up; keying on data.id still bumps load when a row appears.
    const dataRowKey = isValidId(selectedSection?.data?.id)
      ? String(selectedSection?.data?.id)
      : "none";
    const contextKey = `${selectedSection?.id ?? ""}|${String(pageComponentId ?? "")}|${dataRowKey}`;
    const contextBumped = form3DataContextRef.current !== contextKey;
    if (contextBumped) {
      form3DataContextRef.current = contextKey;
    }
    const shouldLoadComponentFromApi = contextBumped;

    // Trigger conditions mapped to actions
    const triggers = {
      loadComponent: {
        condition: shouldLoadComponentFromApi,
        action: loadComponentData,
      },
      hasComponentData: {
        condition:
          !sectionKeyChanged &&
          !widgetChanged &&
          selectedSection?.componentId &&
          pageComponentId,
        action: fetchData,
      },
      needsCategories: {
        condition:
          !sectionKeyChanged &&
          !widgetChanged &&
          pageComponentId &&
          formState.parentId !== null &&
          formState.categories.length === 0 &&
          fetchCategoriesMethod,
        action: () => fetchData(true),
      },
    };

    // Execute first matching trigger
    const trigger = Object.values(triggers).find((t) => t.condition);
    trigger?.action();

    prevLoadedSectionIdRef.current = selectedSection?.id;
  }, [
    pageComponentId,
    selectedSection?.id,
    selectedSection?.componentId,
    formState.parentId,
    formState.categories.length,
    fetchCategoriesMethod,
    loadComponentData,
    fetchData,
    prevPageComponentIdRef,
  ]);

  return { fetchData, handleComponentFieldsUpdate, handleComponentSave, ensureComponentParent };
};
